import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';
import {DefaultMap} from 'external/dev_april_corgi+/js/common/collections';

import {ActionsBuffer} from '../execution/actions_dispatcher';
import {PipeId, NodeSpec} from '../execution/graph';
import {REMAINDER_WELL_VOLUME_NL} from '../execution/opentrons';
import {
  PTransformer,
  PipeContext,
  Source,
  walkMaterials,
} from '../execution/pipes';
import {
  ArrayValue,
  ConcentrationValue,
  DataValue,
  FloatValue,
  IntValue,
  PlateValue,
  PlatedMaterialValue,
  SelectValue,
  VolumeValue,
} from '../types/data';

import {
  MINIMUM_PIPETTE_VOLUME_NL,
  createPipettePlanNote,
  reduceMaterial,
} from './mixing_transformer';
import {makeWaterPlatedMaterial} from './sources';

const LINEAR = 'linear';
const LOGARITHMIC = 'logarithmic';

export const NODE = {
  id: 'node_serial_dilutions',
  label: 'Serial dilutions',
  inputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'spacing',
      label: 'Spacing',
      type: {
        kind: 'select',
        nullable: false,
        options: [LINEAR, LOGARITHMIC],
      },
    },
    {
      id: 'from',
      label: 'From',
      type: {
        kind: 'float',
        minimum: 1,
        maximum: undefined,
      },
    },
    {
      id: 'to',
      label: 'To',
      type: {
        kind: 'float',
        minimum: 1,
        maximum: undefined,
      },
    },
    {
      id: 'steps',
      label: 'Steps',
      type: {
        kind: 'int',
        minimum: 1,
        maximum: undefined,
      },
    },
    {
      id: 'output_plate',
      label: 'Plate',
      type: {
        kind: 'plate',
        contents: undefined,
      },
    },
    {
      id: 'remainder',
      label: 'Remainder',
      type: {
        kind: 'plated_material',
        material: undefined,
      },
    },
  ],
  outputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
  ],
  pipe: ({node, overlay}) => {
    const materials = overlay.padToSources(
      checkExists(node.inputs.get('materials')),
    ) as Array<Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>>;
    const spacing = overlay.singleValuePadSource(
      checkExists(node.inputs.get('spacing')),
      'select',
      () => ({kind: 'select', value: LOGARITHMIC}),
    );
    const from = overlay.singleValuePadSource(
      checkExists(node.inputs.get('from')),
      'float',
      () => ({kind: 'float', number: 1, preferredDisplay: 'raw'}),
    );
    const to = overlay.singleValuePadSource(
      checkExists(node.inputs.get('to')),
      'float',
      () => ({kind: 'float', number: 10, preferredDisplay: 'raw'}),
    );
    const steps = overlay.singleValuePadSource(
      checkExists(node.inputs.get('steps')),
      'int',
      () => ({kind: 'int', number: 4}),
    );
    const plate = overlay.singleValuePadSource(
      checkExists(node.inputs.get('output_plate')),
      'plate',
      context => ({
        kind: 'plate',
        id: context.lab.presetPlates.fourCelsius,
        wells: new Map(),
      }),
    );
    const remainder = overlay.singleValuePadSource(
      checkExists(node.inputs.get('remainder')),
      'plated_material',
      context => makeWaterPlatedMaterial(context),
    );

    const mix = new SerialDilutionsTransformer(
      overlay.nextIdFor(node),
      from,
      materials,
      plate,
      remainder,
      spacing,
      steps,
      to,
    );
    overlay.setPadSource(checkExists(node.outputs.get('materials')), mix);
  },
} as const satisfies NodeSpec;

export class SerialDilutionsTransformer extends PTransformer<
  DataValue,
  ArrayValue<PlatedMaterialValue>
> {
  constructor(
    id: PipeId,
    private readonly from: Source<FloatValue>,
    private readonly materials: Array<
      Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
    >,
    private readonly plate: Source<PlateValue>,
    private readonly remainder: Source<PlatedMaterialValue>,
    private readonly spacing: Source<SelectValue>,
    private readonly steps: Source<IntValue>,
    private readonly to: Source<FloatValue>,
  ) {
    super(id, [from, ...materials, plate, remainder, spacing, steps, to]);
  }

  async collate(context: PipeContext): Promise<void> {
    const spacing = checkExists(this.values.get(this.spacing));
    const spacingValue = (checkExists(spacing.value) as SelectValue).value;
    const stepsSource = checkExists(this.values.get(this.steps));
    const stepsNumber = (checkExists(stepsSource.value) as IntValue).number;
    const from = checkExists(this.values.get(this.from));
    const fromNumber = (checkExists(from.value) as FloatValue).number;
    const to = checkExists(this.values.get(this.to));
    const toNumber = (checkExists(to.value) as FloatValue).number;
    const plate = checkExists(this.values.get(this.plate));
    const plateId = (checkExists(plate.value) as PlateValue).id;
    const remainder = checkExists(this.values.get(this.remainder));
    const remainderPlated = checkExists(remainder.value) as PlatedMaterialValue;

    if (fromNumber < 1) {
      from.reject("'from' must be >= 1");
      for (const {resolve} of this.values.values()) {
        resolve({});
      }
      return;
    } else if (toNumber < fromNumber) {
      to.reject("'to' must be >= 'from'");
      for (const {resolve} of this.values.values()) {
        resolve({});
      }
      return;
    } else if (stepsNumber < 1) {
      stepsSource.reject('Must take at least one step');
      for (const {resolve} of this.values.values()) {
        resolve({});
      }
      return;
    }

    const steps = [];
    if (spacingValue === LINEAR) {
      const step = (toNumber - fromNumber) / stepsNumber;
      for (let i = 0; i <= stepsNumber; ++i) {
        steps.push(fromNumber + i * step);
      }
    } else if (spacingValue === LOGARITHMIC) {
      const factor = Math.pow(
        Math.E,
        Math.log(toNumber / fromNumber) / stepsNumber,
      );
      for (let i = 0; i <= stepsNumber; ++i) {
        steps.push(fromNumber * Math.pow(factor, i));
      }
    } else {
      spacing.reject(`Unknown spacing value: ${spacingValue}`);
      for (const {resolve} of this.values.values()) {
        resolve({});
      }
      return;
    }

    const materials: PlatedMaterialValue[] = [];
    for (const {value, source, reject} of this.values.values()) {
      if (this.materials.indexOf(source as any) >= 0) {
        walkMaterials(value, reject, v => {
          materials.push(v);
        });
      }
    }

    const startsAtOne = steps[0] === 1;
    if (!startsAtOne) {
      steps.unshift(1);
    }

    const allMade: PlatedMaterialValue[] = [];
    const provided = new Set<PlatedMaterialValue>();
    const inspection = new DefaultMap<string, Map<string, number>>(
      () => new Map(),
    );
    for (const input of materials) {
      let first: ConcentrationValue;
      if (input.material.amount.kind === 'concentration') {
        first = input.material.amount;
      } else {
        first = {kind: 'concentration', number: 1, unit: 'x'};
      }

      for (let i = 0; i < steps.length; ++i) {
        const step = steps[i];
        if (step === 1) {
          allMade.push(input);
          if (startsAtOne) {
            provided.add(input);
          }
          continue;
        }

        const reduced = reduceMaterial(input.material, {
          kind: 'concentration',
          number: first.number / step,
          unit: first.unit,
        });
        const plated: PlatedMaterialValue = {
          kind: 'plated_material',
          material: {
            ...reduced,
            label: reduced.label + `.1/${Math.round(step * 100_000) / 100_000}`,
          },
          plate: plateId,
          well: context.lab.plate(plateId).reserveWell().id,
        };
        allMade.push(plated);
        provided.add(plated);
      }
    }

    const providing = allMade.filter(a => provided.has(a));
    const constraints = await this.gatherConstraints(
      {
        kind: 'array',
        elements: providing,
      },
      context,
    );

    const arrayConstraints = constraints.array ?? [];
    const neededVolumeById = new DefaultMap<string, VolumeValue>(() => ({
      kind: 'volume',
      numberNl: 0,
      preferredUnit: 'ul',
    }));
    for (let i = 0; i < arrayConstraints.length; ++i) {
      const source = arrayConstraints[i] ?? {};
      const need = source.volume?.numberNl ?? 0;
      const id = providing[i].material.id;
      neededVolumeById.get(id).numberNl += need;
    }

    const nls = Array(allMade.length);
    for (let i = allMade.length - 1; i >= 0; --i) {
      const child = allMade[i];
      const childNl = neededVolumeById.get(child.material.id).numberNl;
      if (childNl === 0 || materials.indexOf(child) >= 0) {
        nls[i] = {totalNl: childNl, parentNl: 0, remainderNl: 0};
        neededVolumeById.get(child.material.id).numberNl += childNl;
        continue;
      }

      const childPlate = context.lab.plate(child.plate);
      const dilution = steps[(i % steps.length) - 1] / steps[i % steps.length];
      const totalNl = childNl + REMAINDER_WELL_VOLUME_NL;
      const scaled = Math.max(
        MINIMUM_PIPETTE_VOLUME_NL / totalNl / dilution,
        MINIMUM_PIPETTE_VOLUME_NL / totalNl / (1 - dilution),
        1,
      );
      if (scaled * totalNl > childPlate.maxWellVolumeNl) {
        throw new Error(
          `Dilution for ${child.material.label} exceeds max well volume`,
        );
      }
      const parentNl = scaled * totalNl * dilution;
      const remainderNl = scaled * totalNl * (1 - dilution);
      const parent = allMade[i - 1];
      nls[i] = {totalNl: scaled * totalNl, parentNl, remainderNl};
      neededVolumeById.get(parent.material.id).numberNl += parentNl;
      neededVolumeById.get(remainderPlated.material.id).numberNl += remainderNl;
    }

    const remainderWell = context.lab
      .plate(remainderPlated.plate)
      .well(remainderPlated.well);
    const remainderActions: Array<Parameters<ActionsBuffer['pipette']>> = [];
    const mixActions: Array<Parameters<ActionsBuffer['pipette']>> = [];
    for (let i = 0; i < allMade.length; ++i) {
      const {totalNl, parentNl, remainderNl} = nls[i];
      const child = allMade[i];
      if (totalNl === 0) {
        continue;
      } else if (parentNl === 0) {
        if (provided.has(child)) {
          inspection
            .get(child.material.label)
            .set(child.material.label, totalNl);
        }
        continue;
      }

      const childWell = context.lab.plate(child.plate).well(child.well);
      const parent = checkExists(allMade[i - 1]);
      const parentWell = context.lab.plate(parent.plate).well(parent.well);
      remainderActions.push([
        remainderWell,
        childWell,
        {mix: false, volumeNl: remainderNl},
      ]);
      inspection
        .get(child.material.label)
        .set(remainderPlated.material.label, remainderNl);
      mixActions.push([parentWell, childWell, {mix: true, volumeNl: parentNl}]);
      inspection.get(child.material.label).set(parent.material.label, parentNl);
    }

    context.changes.addNodeNote(
      this.id.node,
      'Pipette plan',
      createPipettePlanNote(materials, providing, inspection),
    );

    const promises = [];
    for (const {source, value, resolve, reject} of this.values.values()) {
      if (
        this.materials.indexOf(source as any) >= 0 ||
        source === this.remainder
      ) {
        promises.push(
          resolve(
            walkMaterials(value, reject, v => {
              if (!v) {
                return {};
              }

              return {volume: neededVolumeById.get(v.material.id)};
            }),
          ),
        );
      } else {
        promises.push(resolve({}));
      }
    }
    await Promise.all(promises);

    const actions = context.dispatcher.actor(this);
    for (const action of [...remainderActions, ...mixActions]) {
      actions.pipette(...action);
    }
  }
}
